import { BrandColorMappingItem } from "@/app/models/product/types";
import dataService from "@/app/services/dataService";
import { RootState } from "@/app/store/store";
import { isSAPSource } from "@/app/utils/erpHelper";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

interface ColorMatchSidebarProps {
  productId: number;
  isOpen: boolean;
  onClose: () => void;
}

export const ColorMatchSidebar: React.FC<ColorMatchSidebarProps> = ({
  productId,
  isOpen,
  onClose,
}) => {
  const [mappings, setMappings] = useState<BrandColorMappingItem[]>([]);
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedColorName, setSelectedColorName] = useState("");
  const [colorsForBrand, setColorsForBrand] = useState<BrandColorMappingItem[]>(
    []
  );
  const [match, setMatch] = useState<BrandColorMappingItem | null>(null);
  const [isMatchConfirmed, setIsMatchConfirmed] = useState(false);
  const navigate = useNavigate();
  let warehouseSource = useSelector(
    (state: RootState) => state.warehouseState?.selectedWarehouse?.source
  );
  let isSapWarehouse = isSAPSource(warehouseSource ?? "");
  const isAuthenticated = useSelector(
    (state: RootState) => state.userState.isAuthenticated
  );

  useEffect(() => {
    getColors(productId);
  }, [productId]);

  useEffect(() => {
    if (isOpen) {
      window.scrollTo({ top: 0, behavior: "smooth" });

      setSelectedBrand("");
      setSelectedColorName("");
      setMatch(null);
      setIsMatchConfirmed(false);
    }
  }, [isOpen]);

  const getColors = async (id: number) => {
    const response = await dataService.getProductBrandColors(id);
    if (response?.success && response.data?.mappings?.length > 0) {
      setMappings(response.data.mappings);
    }
  };

  const handleBrandChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const brand = e.target.value;
    setSelectedBrand(brand);
    setSelectedColorName("");
    setMatch(null);
    setIsMatchConfirmed(false);

    const filtered = mappings.filter((m) => m.brand === brand);
    setColorsForBrand(filtered);
  };

  const handleColorChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const color = e.target.value;
    setSelectedColorName(color);
    setIsMatchConfirmed(false);
  };

  const handleFindMatch = async () => {
    if (selectedBrand && selectedColorName) {
      const matched = colorsForBrand.find(
        (m) => m.colorName === selectedColorName
      );
      setMatch(matched || null);
      setIsMatchConfirmed(true);

      if (matched?.productId && isAuthenticated)
        await dataService.sendColorMatchToolSSE(matched?.productId);
    }
  };

  return (
    <div className={`color-match-sidebar sidebar ${isOpen ? "open" : ""}`}>
      <div className="sidebarClose" onClick={onClose}>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="32"
          height="32"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
        </svg>
      </div>
      <div className="color-sidebar-body sidebarBody">
        <div className="sidebar-header">
          <svg
            style={{ width: 26 }}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 32 32"
          >
            <path
              fill="#fddd56"
              d="M27,5c-2.9-2.9-6.8-4.5-11-4.5v7c2.3,0,4.4,0.9,6,2.5s2.5,3.8,2.5,6h7C31.5,11.9,29.9,7.9,27,5z"
            />
            <path
              fill="#70cadb"
              d="M5,5c-2.9,2.9-4.5,6.8-4.5,11h7c0-2.3,0.9-4.4,2.5-6s3.8-2.5,6-2.5v-7C11.9,0.5,7.9,2.1,5,5z"
            />
            <path
              fill="#b9d34c"
              d="M10,22c-1.6-1.6-2.5-3.8-2.5-6h-7c0,4.1,1.6,8.1,4.5,11s6.8,4.5,11,4.5v-7C13.7,24.5,11.6,23.6,10,22z"
            />
            <path
              fill="#ee654b"
              d="M24.5,16c0,2.3-0.9,4.4-2.5,6s-3.8,2.5-6,2.5v7c4.1,0,8.1-1.6,11-4.5s4.5-6.8,4.5-11H24.5z"
            />
            <path
              fill="#9e61a4"
              d="M16,0.5v7c-2.3,0-4.4,0.9-6,2.5L5,5C7.9,2.1,11.9,0.5,16,0.5z"
            />
            <path
              fill="#70cadb"
              d="M10,10c-1.6,1.6-2.5,3.8-2.5,6h-7c0-4.1,1.6-8.1,4.5-11L10,10z"
            />
            <path
              fill="#55bb8c"
              d="M10,22l-5,5c-2.9-2.9-4.5-6.8-4.5-11h7C7.5,18.3,8.4,20.4,10,22z"
            />
            <path
              fill="#b9d34c"
              d="M16,24.5v7c-4.1,0-8.1-1.6-11-4.5l4.9-5C11.6,23.6,13.7,24.5,16,24.5z"
            />
            <path
              fill="#ed4569"
              d="M27,27c-2.9,2.9-6.8,4.5-11,4.5v-7c2.3,0,4.4-0.9,6-2.5L27,27z"
            />
            <path
              fill="#ee654b"
              d="M31.5,16c0,4.1-1.6,8.1-4.5,11l-5-5c1.6-1.6,2.5-3.8,2.5-6H31.5z"
            />
            <path
              fill="#f8a839"
              d="M31.5,16h-7c0-2.3-0.9-4.4-2.5-6l5-5C29.9,7.9,31.5,11.9,31.5,16z"
            />
            <path
              fill="#fddd56"
              d="M27,5l-5,5c-1.6-1.6-3.8-2.5-6-2.5v-7C20.1,0.5,24.1,2.1,27,5z"
            />
          </svg>
          <h3 className="title">Color Match Tool</h3>
        </div>

        <hr style={{ marginBottom: "20px" }} />

        {/* Selection View */}
        {!isMatchConfirmed && (
          <>
            <label>Manufacturer</label>
            <select
              className="dropdown"
              style={{ marginBottom: "20px" }}
              value={selectedBrand}
              onChange={handleBrandChange}
            >
              <option value="">Select Manufacturer</option>
              {Array.from(new Set(mappings.map((m) => m.brand)))
                .sort((a, b) => a.localeCompare(b))
                .map((brand, index) => (
                  <option key={index} value={brand}>
                    {brand}
                  </option>
                ))}
            </select>

            {selectedBrand && (
              <>
                <label>Color</label>
                <select
                  className="dropdown"
                  value={selectedColorName}
                  onChange={handleColorChange}
                >
                  <option value="">Select Color</option>
                  {colorsForBrand
                    .slice()
                    .sort((a, b) => a.colorName.localeCompare(b.colorName))
                    .map((m, index) => (
                      <option key={index} value={m.colorName}>
                        {m.colorName}
                      </option>
                    ))}
                </select>
              </>
            )}

            <div className="buttons">
              <button className="whiteButtonOutline" onClick={onClose}>
                CLOSE
              </button>
              <button
                className="blueButtonOutline"
                style={{ marginLeft: "10px" }}
                onClick={handleFindMatch}
                disabled={!selectedBrand || !selectedColorName}
              >
                FIND MY MATCH
              </button>
            </div>
          </>
        )}

        {/* Match Result View */}
        {isMatchConfirmed && match && (
          <div className="match-result" style={{ marginTop: "20px" }}>
            <h3>Manufacturer Recommended Match</h3>
            <div style={{ display: "flex", alignItems: "center" }}>
              <div
                className="box-product-color"
                style={{ backgroundColor: match.rgb }}
              ></div>
              <div
                style={{
                  font: "normal 14px 'Montserrat', Verdana, sans-serif",
                }}
              >
                <b>
                  {match.brand} {match.colorName}
                </b>
                <br />
                <p style={{ margin: "16px 0px 6px 0" }}>
                  Color Code: <b>#{match.colorCode}</b>
                </p>
                <p style={{ margin: "0 6px 0 0" }}>
                  Lansing SKU:{" "}
                  <b>{isSapWarehouse ? match.sapProduct : match.sku}</b>
                </p>
              </div>
            </div>

            <div className="buttons">
              <button className="whiteButtonOutline" onClick={onClose}>
                CLOSE
              </button>
              <button
                className="blueButtonOutline"
                style={{ marginLeft: "10px" }}
                onClick={() => {
                  var productUrl = `/product?id=${productId}&associatedproduct=${match.productId}`;
                  navigate(productUrl);
                  onClose();
                }}
              >
                SELECT THIS COLOR
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
